import { CreateWorkflowInputType } from "@avenueops/shared-types";
import { PostgresQueryCompiler, QueryAst } from "@avenueops/visual-query-editor";
import { Button } from "@avenueops/web-components";
import { ArrowDownTrayIcon, ChevronRightIcon, CircleStackIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { zodResolver } from "@hookform/resolvers/zod";
import { ConnectionType } from "@prisma/client";
import QueryPreviews from "components/molecules/queryBuilder/QueryPreviews";
import VisualQueryEditor from "components/visual-query-editor/VisualQueryEditor";
import { useQueryRunResults } from "context/QueryRunContext";
import { debounce } from "lodash";
import moment from "moment";
import { useRouter } from "next/router";
import { useCallback, useEffect, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { Oval } from "react-loader-spinner";
import useDeepCompareEffect, { useDeepCompareEffectNoCheck } from "use-deep-compare-effect";
import { pagesPath } from "utils/$path";
import { trpc, trpc2 } from "utils/trpc";
import { z } from "zod";

const zodFormSchema = z.object({
  databaseId: z.string(),
  visualQueryBlob: QueryAst,
});

interface Props {
  defaultValues: Partial<CreateWorkflowInputType>;
  onAutoSave: (data: any) => Promise<void>;
  onAdvance: (data: any) => void;
}

export default function CreateVisualQuery({ defaultValues, onAutoSave, onAdvance }: Props) {
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const router = useRouter();
  const formMethods = useForm({ resolver: zodResolver(zodFormSchema), defaultValues });
  const { data: rawConnections, isLoading: isLoadingConnections } = trpc2.v2.databaseConnections.list.useQuery({});
  const connections = rawConnections?.filter((connection) => connection?.connectionType === ConnectionType.POSTGRES && !!connection.schema);

  const formData = useWatch({ control: formMethods.control });
  const databaseConnectionId = formData.databaseId;
  const monitorTitle = formData.title;

  const debouncedSave = useCallback(
    debounce(
      async (data: any) => {
        await onAutoSave(data);
        setSaving(false);
        setLastSavedAt(new Date());
      },
      1000,
      {
        trailing: true,
      },
    ),
    [onAutoSave, setSaving, setLastSavedAt],
  );
  useEffect(() => {
    if (formMethods.formState.isDirty) {
      setSaving(true);
      debouncedSave.cancel();
      debouncedSave(formData);
    }
  }, [formData]);

  const currentConnection = connections?.find((connection) => connection.id === databaseConnectionId);
  if (isLoadingConnections) {
    return (
      <div className="w-screen h-screen">
        <Oval color="#006BE6" secondaryColor="#006BE6" height={12} width={12} />
      </div>
    );
  }

  return (
    <FormProvider {...formMethods}>
      <form
        className="w-screen h-screen overflow-scroll relative"
        onSubmit={formMethods.handleSubmit((data) => {
          const VisQuery = new PostgresQueryCompiler(data.visualQueryBlob as QueryAst);
          const query = VisQuery.compile();
          onAdvance({ ...formData, query });
        })}
      >
        <div className="sticky top-0 left-0 right-0 bg-background-light flex flex-col md:flex-row py-4">
          <div className="flex-1 pl-10 pr-1 flex items-center gap-2">
            <Button
              type="TEXT"
              onClick={() => {
                debouncedSave(formData);
                router.push(pagesPath.monitors.$url());
              }}
            >
              <XMarkIcon className="w-5 h-5" />
              Save & Exit
            </Button>
            <div className="flex text-center items-center w-4 h-4">
              <div className="bg-gray-900 w-[1px] h-full" />
            </div>
            <div className="flex items-center gap-2 text-gray-400">
              <CircleStackIcon className="w-4 h-4" />
              {currentConnection?.displayName ?? "Select a connection"}
            </div>
            <div className="w-4 h-4">
              <ChevronRightIcon className="w-4 h-4 text-gray-600" />
            </div>
            <div>{monitorTitle}</div>
          </div>
          <div className="pr-10 pl-1 flex justify-end gap-2">
            {saving ? (
              <div className="flex gap-1 items-center">
                <div className="text-gray-600 text-sm">Saving</div>
                <Oval color="#006BE6" secondaryColor="#006BE6" height={12} width={12} />
              </div>
            ) : lastSavedAt ? (
              <div className="flex gap-1 items-center">
                <div className="text-gray-600 text-sm">{`Last saved ${moment.duration(new Date().getTime() - lastSavedAt.getTime()).humanize()} ago`}</div>
                <ArrowDownTrayIcon className="text-gray-600 h-4 w-4 cursor-pointer" />
              </div>
            ) : null}
            <Button type="SECONDARY" submit>
              Continue
            </Button>
          </div>
        </div>
        <VisualQueryEditor datasourceIdName="databaseId" prefix="visualQueryBlob" connections={connections ?? []} />
      </form>
    </FormProvider>
  );
}
